import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import {
  Header,
  Icon,
  Segment,
  Statistic,
  Grid,
  Form,
  Message,
  Progress,
} from 'semantic-ui-react';
import { SG_GET_SLACK_TASK_METRICS } from 'constants/actions';
import Navigation from './Navigation';
import { useWorkspaceId } from './useWorkspaceId';

function Metrics() {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  
  const { taskMetrics, taskMetricsPending } = useSelector(
    (s) => s.slack_tasks || {},
    shallowEqual
  );

  const [dateRange, setDateRange] = useState({
    startDate: getMonthStart(),
    endDate: formatDate(new Date()),
  });

  useEffect(() => {
    if (workspaceId) {
      fetchMetrics();
    }
  }, [dateRange, workspaceId]);

  const fetchMetrics = () => {
    if (!workspaceId) return;
    
    const params = new URLSearchParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }).toString();

    dispatch({
      type: SG_GET_SLACK_TASK_METRICS,
      payload: { workspaceId, queryParams: params },
    });
  };

  const handleDateChange = (e) => {
    setDateRange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const metrics = taskMetrics || {};
  const totals = metrics.totals || {};
  const byPriority = metrics.byPriority || {};

  // Calculate totals for percentages
  const totalTasks = (totals.created || 0) + (totals.completed || 0) + (totals.inProgress || 0) + (totals.todo || 0);
  const priorityTotal = (byPriority.urgent || 0) + (byPriority.high || 0) + (byPriority.medium || 0) + (byPriority.low || 0);

  const getPercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <Page>
      <Navigation />

      <Header as="h1">
        <Icon name="chart bar" />
        Task Metrics & Analytics
        <Header.Subheader>
          Performance metrics for {dateRange.startDate} to {dateRange.endDate}
        </Header.Subheader>
      </Header>

      {/* Date Range Filter */}
      <Segment>
        <Form>
          <Form.Group>
            <Form.Input
              label="Start Date"
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              width={4}
            />
            <Form.Input
              label="End Date"
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              width={4}
            />
          </Form.Group>
        </Form>
      </Segment>

      {taskMetricsPending ? (
        <Message icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Loading metrics...</Message.Header>
          </Message.Content>
        </Message>
      ) : (
        <>
          {/* Key Metrics */}
          <Segment>
            <Header as="h3">Overview</Header>
            <Grid columns={4} stackable>
              <Grid.Column>
                <Statistic size="small" color="blue">
                  <Statistic.Value>
                    <Icon name="tasks" />
                    {totals.created || 0}
                  </Statistic.Value>
                  <Statistic.Label>Tasks Created</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="small" color="green">
                  <Statistic.Value>
                    <Icon name="check circle" />
                    {totals.completed || 0}
                  </Statistic.Value>
                  <Statistic.Label>Completed</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="small" color="orange">
                  <Statistic.Value>
                    <Icon name="spinner" />
                    {totals.inProgress || 0}
                  </Statistic.Value>
                  <Statistic.Label>In Progress</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="small">
                  <Statistic.Value>{metrics.completionRate || 0}%</Statistic.Value>
                  <Statistic.Label>Completion Rate</Statistic.Label>
                </Statistic>
              </Grid.Column>
            </Grid>
          </Segment>

          {/* Visual Charts with Progress Bars */}
          <Grid columns={2} stackable>
            <Grid.Column>
              <Segment>
                <Header as="h3">Tasks by Priority</Header>
                <ProgressList>
                  <ProgressItem>
                    <ProgressLabel>
                      <span>🔴 Urgent</span>
                      <strong>{byPriority.urgent || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(byPriority.urgent || 0, priorityTotal)}
                      color="red"
                      size="small"
                    />
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>
                      <span>🟠 High</span>
                      <strong>{byPriority.high || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(byPriority.high || 0, priorityTotal)}
                      color="orange"
                      size="small"
                    />
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>
                      <span>🟡 Medium</span>
                      <strong>{byPriority.medium || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(byPriority.medium || 0, priorityTotal)}
                      color="yellow"
                      size="small"
                    />
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>
                      <span>⚪ Low</span>
                      <strong>{byPriority.low || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(byPriority.low || 0, priorityTotal)}
                      color="grey"
                      size="small"
                    />
                  </ProgressItem>
                </ProgressList>
              </Segment>
            </Grid.Column>

            <Grid.Column>
              <Segment>
                <Header as="h3">Tasks by Status</Header>
                <ProgressList>
                  <ProgressItem>
                    <ProgressLabel>
                      <span><Icon name="plus circle" color="blue" />Created</span>
                      <strong>{totals.created || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(totals.created || 0, totalTasks)}
                      color="blue"
                      size="small"
                    />
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>
                      <span><Icon name="check circle" color="green" />Completed</span>
                      <strong>{totals.completed || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(totals.completed || 0, totalTasks)}
                      color="green"
                      size="small"
                    />
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>
                      <span><Icon name="spinner" color="orange" />In Progress</span>
                      <strong>{totals.inProgress || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(totals.inProgress || 0, totalTasks)}
                      color="orange"
                      size="small"
                    />
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>
                      <span><Icon name="circle outline" />To Do</span>
                      <strong>{totals.todo || 0}</strong>
                    </ProgressLabel>
                    <Progress
                      percent={getPercentage(totals.todo || 0, totalTasks)}
                      color="grey"
                      size="small"
                    />
                  </ProgressItem>
                </ProgressList>
              </Segment>
            </Grid.Column>
          </Grid>

          {/* Priority Breakdown */}
          <Segment>
            <Header as="h3">Priority Breakdown</Header>
            <PriorityGrid>
              <PriorityCard urgent>
                <PriorityIcon>🔴</PriorityIcon>
                <div>
                  <PriorityValue>{byPriority.urgent || 0}</PriorityValue>
                  <PriorityLabel>Urgent</PriorityLabel>
                </div>
              </PriorityCard>
              <PriorityCard high>
                <PriorityIcon>🟠</PriorityIcon>
                <div>
                  <PriorityValue>{byPriority.high || 0}</PriorityValue>
                  <PriorityLabel>High</PriorityLabel>
                </div>
              </PriorityCard>
              <PriorityCard medium>
                <PriorityIcon>🟡</PriorityIcon>
                <div>
                  <PriorityValue>{byPriority.medium || 0}</PriorityValue>
                  <PriorityLabel>Medium</PriorityLabel>
                </div>
              </PriorityCard>
              <PriorityCard low>
                <PriorityIcon>⚪</PriorityIcon>
                <div>
                  <PriorityValue>{byPriority.low || 0}</PriorityValue>
                  <PriorityLabel>Low</PriorityLabel>
                </div>
              </PriorityCard>
            </PriorityGrid>
          </Segment>

          {/* Performance Insights */}
          <Segment>
            <Header as="h3">
              <Icon name="lightbulb" />
              Insights
            </Header>
            <InsightsList>
              {metrics.completionRate >= 80 && (
                <InsightItem positive>
                  <Icon name="check circle" color="green" />
                  Excellent completion rate! Keep up the good work.
                </InsightItem>
              )}
              {byPriority.urgent > 5 && (
                <InsightItem warning>
                  <Icon name="warning sign" color="orange" />
                  You have {byPriority.urgent} urgent tasks. Consider prioritizing these.
                </InsightItem>
              )}
              {totals.inProgress > 10 && (
                <InsightItem info>
                  <Icon name="info circle" color="blue" />
                  {totals.inProgress} tasks in progress. Focus on completing a few before starting new ones.
                </InsightItem>
              )}
              {totals.created === 0 && (
                <InsightItem>
                  <Icon name="clipboard outline" />
                  No tasks created in this period.
                </InsightItem>
              )}
            </InsightsList>
          </Segment>
        </>
      )}
    </Page>
  );
}

// Helper functions
function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function getMonthStart() {
  const today = new Date();
  return formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
}

const Page = styled.div`
  padding: 20px;
`;

const ProgressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 16px;
`;

const ProgressItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;

  span {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  strong {
    color: #2185d0;
    font-size: 18px;
  }
`;

const PriorityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const PriorityCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: ${(p) => {
    if (p.urgent) return '#fef2f2';
    if (p.high) return '#fff7ed';
    if (p.medium) return '#fefce8';
    if (p.low) return '#f8fafc';
    return '#fff';
  }};
  border: 1px solid ${(p) => {
    if (p.urgent) return '#dc2626';
    if (p.high) return '#ea580c';
    if (p.medium) return '#facc15';
    if (p.low) return '#9ca3af';
    return '#e0e0e0';
  }};
  border-radius: 8px;
`;

const PriorityIcon = styled.div`
  font-size: 32px;
`;

const PriorityValue = styled.div`
  font-size: 24px;
  font-weight: bold;
`;

const PriorityLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const InsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const InsightItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${(p) => {
    if (p.positive) return '#f0fdf4';
    if (p.warning) return '#fff7ed';
    if (p.info) return '#eff6ff';
    return '#f8fafc';
  }};
  border-left: 4px solid ${(p) => {
    if (p.positive) return '#16a34a';
    if (p.warning) return '#ea580c';
    if (p.info) return '#2563eb';
    return '#64748b';
  }};
  border-radius: 4px;
  font-size: 15px;

  i.icon {
    flex-shrink: 0;
  }
`;

export default Metrics;
